/**
 * In-app UI test engine.
 *
 * Drives the REAL application UI the way a user would — moving an on-screen
 * cursor to each target, highlighting it, then clicking/typing — so the Test
 * App genuinely exercises buttons, navigation and screens (and catches UI bugs
 * a backend-only run would miss). Each lab provides a structured step list
 * (see labScripts.js) that this engine executes.
 *
 * Targeting: steps reference elements by `data-testid` (stable) and may add a
 * `text` matcher to disambiguate (e.g. the canvas node labelled "Contacts").
 *
 * React-safe input: setting input.value directly doesn't notify React, so we
 * use the native value setter + dispatch an input event.
 */

import { setDialogAutoConfirm } from './dialog';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// ── Visual overlay (cursor + highlight) ──────────────────────────────────────

let cursorEl = null;
let highlightEl = null;

function ensureOverlay() {
  if (!cursorEl) {
    cursorEl = document.createElement('div');
    cursorEl.setAttribute('data-testid', 'uitest-cursor');
    Object.assign(cursorEl.style, {
      position: 'fixed', width: '22px', height: '22px', zIndex: 2147483647,
      pointerEvents: 'none', transition: 'left 0.45s ease, top 0.45s ease',
      left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))',
    });
    cursorEl.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22"><path d="M4 2l6 16 2.5-6.5L19 9z" fill="#111" stroke="#fff" stroke-width="1.5"/></svg>';
    document.body.appendChild(cursorEl);
  }
  if (!highlightEl) {
    highlightEl = document.createElement('div');
    Object.assign(highlightEl.style, {
      position: 'fixed', zIndex: 2147483646, pointerEvents: 'none',
      border: '2px solid #6366f1', borderRadius: '8px',
      boxShadow: '0 0 0 9999px rgba(15,23,42,0.18)',
      transition: 'all 0.3s ease', opacity: '0',
    });
    document.body.appendChild(highlightEl);
  }
}

export function teardownOverlay() {
  cursorEl?.remove(); highlightEl?.remove();
  cursorEl = null; highlightEl = null;
}

function moveCursorTo(el) {
  ensureOverlay();
  const r = el.getBoundingClientRect();
  cursorEl.style.left = `${r.left + r.width / 2}px`;
  cursorEl.style.top = `${r.top + r.height / 2}px`;
  highlightEl.style.opacity = '1';
  highlightEl.style.left = `${r.left - 4}px`;
  highlightEl.style.top = `${r.top - 4}px`;
  highlightEl.style.width = `${r.width + 4}px`;
  highlightEl.style.height = `${r.height + 4}px`;
}

// ── Element finding ──────────────────────────────────────────────────────────

function queryAll(sel) {
  try { return Array.from(document.querySelectorAll(sel)); } catch { return []; }
}

function matchEl(sel, text) {
  const els = queryAll(sel);
  if (!text) return els[0] || null;
  const lc = String(text).toLowerCase();
  return els.find((e) => (e.textContent || '').toLowerCase().includes(lc))
    || els.find((e) => (e.value || '').toLowerCase().includes(lc))
    || null;
}

async function waitForEl(sel, text, timeoutMs = 15000) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const el = matchEl(sel, text);
    if (el && el.offsetParent !== null) return el; // visible
    await sleep(250);
  }
  return null;
}

// ── React-safe input ─────────────────────────────────────────────────────────

function setNativeValue(el, value) {
  const proto = el.tagName === 'TEXTAREA'
    ? window.HTMLTextAreaElement.prototype
    : el.tagName === 'SELECT'
      ? window.HTMLSelectElement.prototype
      : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  setter ? setter.call(el, value) : (el.value = value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

// ── Step execution ───────────────────────────────────────────────────────────
// A step: { do, sel?, testid?, text?, value?, contains?, say, timeout? }
// Supported `do`: navigate | click | type | select | waitFor | expect | sleep

function selOf(step) {
  if (step.testid) return `[data-testid="${step.testid}"]`;
  return step.sel || '*';
}

export async function runStep(step, opts = {}) {
  const { pause = 350 } = opts;
  const label = step.say || step.do;

  if (step.do === 'sleep') { await sleep(step.value || 500); return { ok: true, detail: label }; }

  if (step.do === 'navigate') {
    const el = await waitForEl(`[data-testid="nav-${step.to}"]`, null, step.timeout);
    if (!el) return { ok: false, detail: `Could not find navigation for "${step.to}"` };
    moveCursorTo(el); await sleep(pause); el.click();
    return { ok: true, detail: label };
  }

  if (step.do === 'expect') {
    const el = await waitForEl(selOf(step), step.contains, step.timeout);
    return el ? { ok: true, detail: label } : { ok: false, detail: `Expected ${step.contains ? `"${step.contains}" in ` : ''}${selOf(step)} but it never appeared` };
  }

  if (step.do === 'waitFor') {
    const el = await waitForEl(selOf(step), step.text, step.timeout);
    return el ? { ok: true, detail: label } : { ok: false, detail: `Timed out waiting for ${selOf(step)}` };
  }

  // Interactive steps need the element present + visible first.
  const el = await waitForEl(selOf(step), step.text, step.timeout);
  if (!el) return { ok: false, detail: `Could not find ${selOf(step)}${step.text ? ` ("${step.text}")` : ''}` };
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  await sleep(150);
  moveCursorTo(el);
  await sleep(pause);

  if (step.do === 'click') { el.click(); return { ok: true, detail: label }; }
  if (step.do === 'type') { el.focus(); setNativeValue(el, step.value ?? ''); return { ok: true, detail: label }; }
  if (step.do === 'select') { setNativeValue(el, step.value ?? ''); return { ok: true, detail: label }; }

  return { ok: false, detail: `Unknown step "${step.do}"` };
}

// In-app confirm dialogs (e.g. lab Deploy) can't be clicked by an in-app
// robot, so auto-accept them for the duration of a run.
export function withAutoConfirm(fn) {
  setDialogAutoConfirm(true);
  return Promise.resolve()
    .then(fn)
    .finally(() => setDialogAutoConfirm(false));
}
